import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Typography } from '@material-ui/core';
import clsx from 'clsx';

import {
  BookingRequest,
  bookingService,
  ContactType,
  FormStatus,
  isEmployeeBookingAllowed,
  propertyQuery,
  ReservationForms,
  reservationService,
  RoomRateForms,
  roomRateService,
  RoomType,
  roomTypeQuery,
  sessionQuery,
} from '@lib/state';
import {
  FormHub,
  getAcceptCardData,
  LocationForm,
  LocationFormFields,
  PaymentDetailsForm,
  ReservationConfirmation,
  RoomRateForm,
  useBookingStatus,
  useCommonStyle,
  useFormEvents,
  useObservable,
  usePageTitle,
  useRoomRates,
  withPropertyFeatures,
} from '@lib/common';
import { BreadcrumbBackButton, isFrontDesk } from 'app/shared';
import { GuestLookupForm } from '../components';
import { SelectCorporateDelegateForm } from '../components/select-corporate-delegate.form';
import { GuestProfileModel } from '@lib/state/api/generated/reservations';
const CreateReservation: React.FC = () => {
  const commonStyles = useCommonStyle();
  usePageTitle('Add Reservation');

  const [corporateAccountSelected, setCorporateAccountSelected] = useState<string | undefined>(
    undefined
  );
  const [roomType, setRoomType] = useState<RoomType | undefined>(undefined);
  const [editLocation, setEditLocation] = useState(true);
  const [requiresPayment, setRequiresPayment] = useState(true);
  const [locationFormError, setLocationFormError] = useState<Error | undefined>(undefined);

  const [delegateGuest, setDelegateGuest] = useState<GuestProfileModel | undefined>(undefined);
  const [readyForCheckAvailability, setReadyForCheckAvailability] = useState<boolean | undefined>(
    true
  );
  const [delegateUserId, setDelegateUserId] = useState<string | undefined>(undefined);
  const [bookingContactType, setBookingContactType] = useState<ContactType | undefined>(undefined);
  const [userId, setUserId] = useState<string | null>(null);

  const [formEvent, resetReservationFormStatus] = useFormEvents(ReservationForms.CreateReservation);

  const [{ error: availabilityError, status: availabilityStatus }] = useFormEvents(
    RoomRateForms.Availability
  );

  const propertyId = useObservable(sessionQuery.propertyId);
  const properties = useObservable(propertyQuery.properties);
  const roomTypes = useObservable(roomTypeQuery.propertyRoomTypes);

  const bookingStatus = useBookingStatus();
  const defaultRoomType = useMemo(() => {
    const lowestValue = Math.min(...roomTypes.map(r => r.sortOrder ?? 0));
    return roomTypes.find(r => r.sortOrder === lowestValue);
  }, [roomTypes]);

  const {
    uiState,
    roomRates,
    checkInStartOfDay,
    checkOutStartOfDay,
    checkInDefaultDate,
    corporateBooking,
    costCenterOptions,
    quantity,
    policy,
    guestsPerRoom,
    loading,
  } = useRoomRates(userId, corporateAccountSelected);

  const [didSelectDates, setDidSelectDates] = useState(
    !!(uiState.checkInDate && uiState.checkOutDate)
  );

  const roomTypeId = useMemo(() => {
    return uiState.roomTypeId;
  }, [uiState]);

  const isManagerOverrideRequired = useMemo(
    () =>
      bookingContactType !== ContactType.Guest &&
      !isEmployeeBookingAllowed(corporateBooking, checkOutStartOfDay),
    [corporateBooking, checkOutStartOfDay, bookingContactType]
  );

  const delegateError = useMemo(() => {
    if (userId && delegateUserId !== userId && corporateBooking) {
      setEditLocation(true);
      setDidSelectDates(false);
      return {
        message:
          'To book a corporate reservation, please select the corporate account and delegate here.',
      } as Error;
    }
    return undefined;
  }, [delegateUserId, userId, corporateBooking]);

  useEffect(() => {
    const roomType = roomTypes.find(r => r.id === roomTypeId);
    setRoomType(roomType);
  }, [roomTypeId, roomTypes]);

  const selectedRatePlan = useMemo(() => roomRates?.find(r => r.roomTypeId === roomType?.id), [
    roomRates,
    roomType,
  ]);

  const corporateAccountId = useMemo(() => {
    return corporateBooking?.id;
  }, [corporateBooking]);

  useEffect(() => {
    //reset the reservation form status when the quantity is changed
    resetReservationFormStatus();
  }, [quantity, resetReservationFormStatus]);

  useEffect(() => {
    if (availabilityStatus === FormStatus.Success && didSelectDates) {
      setEditLocation(false);
    }
  }, [availabilityStatus, didSelectDates]);

  useEffect(
    () => () => {
      reservationService.selectReservation();
      roomRateService.selectRoomRate();
      roomRateService.resetUI();
    },
    []
  );

  const submitLocationForm = useCallback(
    (values: LocationFormFields) => {
      // set the userId first so that it is available for the room rate query
      setUserId(delegateGuest?.userId ?? null);
      const newRoomType = roomTypes.find(r => r.id === values.roomTypeId);
      roomRateService.updateUI({ ...values, guestsPerRoom: newRoomType?.totalOccupancy ?? 1 });
      setDidSelectDates(true);
      resetReservationFormStatus();
      setLocationFormError(undefined);
    },
    [resetReservationFormStatus, delegateGuest, roomTypes, setLocationFormError]
  );

  const submit = useCallback(
    ({ card, ...booking }: any) => {
      const reservationQuantity = !booking.guestTbd
        ? booking.quantity * guestsPerRoom
        : booking.quantity * guestsPerRoom - 1;

      const request: BookingRequest = {
        card: getAcceptCardData(card),
        ...booking,
        quantity: reservationQuantity,
        corporateAccountId: corporateAccountId,
        rooms: booking.guestTbd
          ? booking.rooms.filter(
              (r: { group: string }) => r.group !== reservationQuantity.toString()
            )
          : booking.rooms,
        fields: booking.fields?.filter((f: { key: string }) => f.key) ?? [],
      };

      if (userId) request.contact.userId = userId;
      if (uiState.promoCode) request.promoCode = uiState.promoCode;

      bookingService.createReservationForGuest(
        request,
        !!corporateAccountSelected || request.contact.contactType === ContactType.Delegate
      );
    },
    [uiState, userId, corporateAccountId, guestsPerRoom, corporateAccountSelected]
  );

  const onRateFormLoadFail = useCallback(() => {
    //Prevent from firing early
    if (!editLocation) {
      setLocationFormError(new Error('There was an error. Please try again.'));
      setEditLocation(true);
      setDidSelectDates(false);
    }
  }, [setDidSelectDates, setEditLocation, setLocationFormError, editLocation]);

  return (
    <Container maxWidth="lg">
      <BreadcrumbBackButton destination="/reservations" />
      <Typography
        variant="h4"
        color="textPrimary"
        align="center"
        paragraph
        className={clsx({ [commonStyles.hidden]: !editLocation })}
      >
        Select the dates for your stay.
      </Typography>
      {bookingStatus ? (
        <ReservationConfirmation
          reservations={bookingStatus.reservations ?? []}
          failures={bookingStatus.failures ?? []}
          consumedHolds={bookingStatus.consumedHolds}
          properties={properties}
          policy={policy}
        />
      ) : (
        <FormHub onSubmit={submit}>
          <SelectCorporateDelegateForm
            className={clsx({ [commonStyles.hidden]: !editLocation })}
            onSelectDelegateGuest={(isReadyForCheckAvailability, corporateAccountId, guest) => {
              setCorporateAccountSelected(corporateAccountId);
              setDelegateUserId(guest?.userId);
              setReadyForCheckAvailability(isReadyForCheckAvailability);
              setDelegateGuest(guest);
            }}
          />
          <LocationForm
            //Component props
            className={clsx({
              [commonStyles.hidden]: !editLocation,
            })}
            itemSpacing={3}
            //Reservation details props
            properties={properties}
            minCheckIn={checkInDefaultDate}
            roomTypes={roomTypes}
            defaultCheckIn={checkInStartOfDay}
            defaultCheckOut={checkOutStartOfDay}
            defaultPropertyId={propertyId}
            defaultCorporateAccountId={corporateAccountId}
            defaultRoomTypeId={defaultRoomType?.id}
            //Form props
            onStepComplete={submitLocationForm}
            status={availabilityStatus}
            error={availabilityError ?? delegateError}
            hideLocationForPropertyId
            readyForCheckAvailability={readyForCheckAvailability}
          />
          <Container className={editLocation ? commonStyles.hidden : undefined} disableGutters>
            {quantity && roomRates?.length > 0 && (
              <>
                <RoomRateForm
                  //Component props
                  usePaper={false}
                  variant="contained"
                  //Reservation details props
                  properties={properties}
                  selectedRate={selectedRatePlan}
                  policy={policy}
                  roomType={roomType}
                  quantity={quantity * guestsPerRoom}
                  //Form props
                  onEditLocation={() => {
                    setEditLocation(true);
                    setDidSelectDates(false);
                  }}
                  onLoadFail={onRateFormLoadFail}
                  setRequiresPayment={setRequiresPayment}
                  status={availabilityStatus}
                  availabilityError={availabilityError ?? locationFormError}
                  isLoading={loading || availabilityStatus === FormStatus.Pending}
                  isSingle={guestsPerRoom === 1}
                />
                <GuestLookupForm
                  variant="contained"
                  setUserId={setUserId}
                  setBookingContactType={setBookingContactType}
                  quantity={quantity}
                  corporateBooking={corporateBooking}
                  costCenterOptions={costCenterOptions}
                  guestCount={roomType?.adultOccupancy?.maxOccupancy ?? undefined}
                  useRearCam={isFrontDesk()}
                  delegateGuest={delegateGuest}
                  guestsPerRoom={guestsPerRoom}
                />
              </>
            )}
            <PaymentDetailsForm
              {...formEvent}
              requiresPayment={requiresPayment}
              useGuestPaymentMethods={true}
              isManagerOverrideRequired={isManagerOverrideRequired}
              guestUserId={userId}
              variant="contained"
              error={formEvent?.error ?? availabilityError}
              corporateAccountId={corporateAccountId}
            />
          </Container>
        </FormHub>
      )}
    </Container>
  );
};

export const AddReservationPage = withPropertyFeatures(CreateReservation, sessionQuery.propertyId);
